import { useEffect, useRef } from 'react';

import posthog from 'posthog-js';
import { useAppSelector } from 'shared/hooks/redux';

/** Super property on every event, so the shared PostHog project can tell the backoffice apart from lidz_front. */
export const ANALYTICS_APP = 'backoffice';

/**
 * Initializes PostHog when both key and host are configured. Builds without a key (local, staging, previews) skip
 * tracking entirely. Uses the same project as lidz_front, so a user is one person across both apps.
 * @returns Whether PostHog was initialized.
 */
export const initAnalytics = (): boolean => {
  const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

  if (!posthogKey || !posthogHost) {
    return false;
  }

  posthog.init(posthogKey, {
    api_host: posthogHost,
    ui_host: 'https://us.posthog.com',
    defaults: '2025-05-24',
    person_profiles: 'identified_only',
    session_recording: { maskAllInputs: true },
  });
  posthog.register({ app: ANALYTICS_APP });

  return true;
};

/**
 * Keeps the PostHog person in sync with the signed-in user: identifies on sign-in (and on session restore) and resets
 * on any sign-out path, since all of them clear the user slice. The distinct id and person property names match
 * lidz_front's identify, so both apps update the same person.
 */
export const useAnalyticsIdentity = (): void => {
  const { id, email, isAdmin } = useAppSelector((state) => state.user);
  const previousId = useRef(id);

  useEffect(() => {
    if (!posthog.__loaded) {
      return;
    }

    if (id) {
      posthog.identify(String(id), { email, isAdmin });
    } else if (previousId.current) {
      posthog.reset();
    }

    previousId.current = id;
  }, [id, email, isAdmin]);
};

export const AnalyticsIdentity = (): null => {
  useAnalyticsIdentity();
  return null;
};
